import autobind from 'autobind-decorator';
import { h, markRaw } from '#imports';
import type { Rule } from 'ant-design-vue/es/form';
import { InfoCircleOutlined } from '@ant-design/icons-vue';
import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import { checkNickNameSensitive } from '@core/api/common';
import {
  fillUserInfo,
  generateUserInfo,
  setUserDefault,
  setUserNickname,
} from '@core/api/user';
import { StorageService } from '@core/services/storage.service';
import { showErrorMsg } from '@core/utils/utility';
import { RouterService } from './router.service';
import { UserService } from './user.service';

const formItemErrorMessageWrapper = (text: string) => {
  return h('div', [
    h(InfoCircleOutlined),
    h(
      'span',
      {
        class: 'ml-1',
      },
      text,
    ),
  ]);
};

@Injectable()
export class ProfileService {
  // 收集编程水平的弹窗
  public programLevelVisible = false;
  public programLevel = 0;
  // 新用户添加老师微信弹窗
  public newUserAddTeacherVisible = false;

  public visible = false;
  public cropping = false;
  public getForm1Ref!: any;

  public loading = false;
  public generating = false;
  public model = {
    avatar: '',
    nickname: '',
  };
  public rules: Record<string, Rule[]> = {
    nickname: [{ validator: this.validateNickname, trigger: 'blur' }],
  };

  @Inject(forwardRef(() => UserService))
  public userService!: UserService;

  @Inject(forwardRef(() => RouterService))
  public routerService!: RouterService;

  @Inject(forwardRef(() => StorageService))
  public storageService!: StorageService;

  public init(ref1: any) {
    this.getForm1Ref = markRaw(ref1);
  }

  public setVisible(v: boolean) {
    // 如果优先显示编程水平的弹窗，则不影响 visible 的值
    if(this.userService.user.windowFlag) {
      this.programLevelVisible = v;
    } else {
      this.visible = v;
    }
  }

  public submit() {
    const params = {
      ...this.model,
      uid: this.userService.user.uid,
    };
    return setUserNickname(params)
      .then(() => {
        this.routerService.reload();
      })
      .catch((e) => {
        showErrorMsg(e);
      });
  }

  @autobind
  public handleSubmit() {
    this.loading = true;
    Promise.all([this.getForm1Ref().validate()])
      .then(() => {
        return this.submit();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  @autobind
  public validateNickname(rule: any, value: any) {
    if (this.generating || this.visible === false) {
      return Promise.resolve();
    }
    if (!value) {
      return Promise.reject(formItemErrorMessageWrapper('请输入昵称'));
    } else if (value.length > 20) {
      return Promise.reject(
        formItemErrorMessageWrapper('昵称不能超过20个字符'),
      );
    } else {
      return checkNickNameSensitive(value)
        .then(() => {
          return Promise.resolve();
        })
        .catch((e) => {
          return Promise.reject(
            formItemErrorMessageWrapper(e.errMsg || '接口异常'),
          );
        });
    }
  }

  @autobind
  public handleLogout() {
    this.setVisible(false);
    this.userService.reset();
  }

  @autobind
  public generateNewUserInfo(onlyNickname = false) {
    this.generating = true;
    return generateUserInfo().then((res) => {
      const { nickname, avatar } = res;
      if (onlyNickname) {
        this.model.nickname = nickname as string;
      } else {
        this.model.nickname = nickname as string;
        this.model.avatar = avatar as string;
      }
    }).catch((e) => {
      showErrorMsg(e);
    }).finally(() => {
      this.generating = false;
    });
  }
  @autobind
  public setDefaultInfo(){
    return setUserDefault().then(() => {
      this.routerService.reload();
    }).catch((e) => {
      showErrorMsg(e);
    });
  }

  @autobind
  public skip() {
    this.setVisible(false);
    this.setDefaultInfo();
  }
}
